import { CircleCheckBig, KeyRound, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useResetPassword } from "~/api/authentication/authentication";
import { useGetPasswordRules } from "~/api/users/users";
import SmartPasswordInput from "~/components/smart-password-input";
import { Button } from "~/components/ui/button";
import { getStoredForgotPasswordEmail, ROUTES } from "~/helpers";
import { useThemedOnboardingImage } from "~/hooks/useThemedOnboardingImage";
import { trackEvent } from "~/utils/analytics";
import { useAuthStepImage } from "../hooks/useAuthStepImage";

enum ResetPasswordFormState {
  CREATE_PASSWORD = "CREATE_PASSWORD",
  SUCCESS = "SUCCESS",
}

export const ResetPasswordForm = () => {
  const [formState, setFormState] = useState<ResetPasswordFormState>(
    ResetPasswordFormState.CREATE_PASSWORD
  );
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const code = searchParams.get("resetCode");

  const { mutate: resetPassword, isPending, isError } = useResetPassword();
  const { data: passwordRules, isLoading: isLoadingPasswordRules } =
    useGetPasswordRules();

  const passwordBg = useThemedOnboardingImage("password");
  const successBg = useThemedOnboardingImage("success");

  const stepImage = useMemo(() => {
    switch (formState) {
      case ResetPasswordFormState.CREATE_PASSWORD:
        return passwordBg;
      case ResetPasswordFormState.SUCCESS:
        return successBg;
      default:
        return passwordBg;
    }
  }, [formState, passwordBg, successBg]);

  useAuthStepImage(stepImage);

  useEffect(() => {
    if (formState === ResetPasswordFormState.SUCCESS) {
      trackEvent({ eventName: "app/reset_password/success" });
    }
  }, [formState]);

  if (!code) {
    return <div>No reset code found</div>;
  }

  const handleSubmit = async (password: string) => {
    trackEvent({ eventName: "app/reset_password/save_password.button/submit" });

    const email = getStoredForgotPasswordEmail() || "";

    resetPassword(
      {
        data: {
          email,
          code,
          newPassword: password,
        },
      },
      {
        onSuccess: () => {
          setFormState(ResetPasswordFormState.SUCCESS);
        },
      }
    );
  };

  const navigateToLogin = () => {
    trackEvent({ eventName: "app/reset_password/log_in.button/click" });
    navigate(ROUTES.LOGIN);
  };

  return (
    <div className="mx-auto max-w-[560px]">
      {isLoadingPasswordRules ? (
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <>
          {formState === ResetPasswordFormState.CREATE_PASSWORD && (
            <div className="flex flex-col gap-4 sm:gap-[24px] w-full">
              <div>
                <KeyRound className="h-[32px] w-[32px] text-[#71717A] mb-4 sm:mb-[24px]" />
                <h3 className="text-2xl font-semibold text-base-foreground pt-2 sm:pt-[16px]">
                  <FormattedMessage
                    id="loginForm.newPassword"
                    defaultMessage="Create New Password"
                  />
                </h3>
                <p className="text-sm text-[#71717A] mt-[16px]">
                  <FormattedMessage
                    id="loginForm.newPasswordSubtitle"
                    defaultMessage="Change your temporary password to a personal and secure one."
                  />
                </p>
              </div>
              <div className="flex flex-col gap-4">
                <SmartPasswordInput
                  onPasswordSubmit={handleSubmit}
                  activeRequirements={passwordRules?.rules || []}
                  isPending={isPending}
                />
              </div>
            </div>
          )}
          {formState === ResetPasswordFormState.SUCCESS && (
            <div className="flex flex-col gap-4 sm:gap-[24px] w-full">
              <div className="flex flex-col">
                <CircleCheckBig className="h-[32px] w-[32px] text-[#71717A] mb-4 sm:mb-[24px]" />
                <h3 className="text-2xl font-semibold text-base-foreground pt-2 sm:pt-[16px]">
                  Success!
                </h3>
                <p className="text-sm text-[#71717A] mt-[16px]">
                  Your password has been updated! You can now log in.
                </p>
              </div>
              <Button className="w-full" onClick={navigateToLogin}>
                Log In
              </Button>
            </div>
          )}
          {isError && (
            <p className="mt-4 text-red-600 text-sm text-center">
              Failed to reset password. Please try again.
            </p>
          )}
        </>
      )}
    </div>
  );
};
